// src/pages/ClientHistory.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { normalizeRole, ROLE_VALUE, getRoleLabel } from '../constants/roleLabels';
import { db } from '../firebase';
import {
  collection,
  query,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
  arrayUnion,
  orderBy,
} from 'firebase/firestore';
import { calculateSummaryStats } from '../services/historyService';
import OperationsShell from '../components/dashboard/shell/OperationsShell';
import IncidentHistoryView from '../components/dashboard/history/IncidentHistoryView';
import { logger } from "../utils/logger";

function ClientHistory() {
  const { currentUser, currentUserRole, logout, ready } = useAuth();
  const [completedProblems, setCompletedProblems] = useState([]);
  const [hiddenHistory, setHiddenHistory] = useState([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    avgRating: 0,
    monthlyData: [],
  });
  const [domains, setDomains] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;

    // Redirect if not client
    if (!currentUser || normalizeRole(currentUserRole) !== ROLE_VALUE.requester) {
      navigate('/login');
      return;
    }

    // Load hiddenHistory first
    const fetchHiddenHistory = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setHiddenHistory(userSnap.data().hiddenHistory || []);
        } else {
          setHiddenHistory([]);
        }
      } catch (err) {
        console.error('Error fetching hiddenHistory:', err);
        setHiddenHistory([]);
      }
    };
    fetchHiddenHistory();

    // Subscribe to user's history subcollection
    const historyRef = collection(db, 'users', currentUser.uid, 'history');
    const q = query(historyRef, orderBy('dateCompleted', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const historyItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        logger.log(`📋 Client history loaded: ${historyItems.length} items`, historyItems);

        // filter out soft-deleted ones
        const visible = historyItems.filter(
          (p) => !hiddenHistory.includes(p.id)
        );

        setCompletedProblems(visible);

        // Extract unique domains
        const uniqueDomains = [...new Set(visible.map((p) => p.domain || 'General'))];
        setDomains(uniqueDomains);

        // Calculate stats
        const summaryStats = await calculateSummaryStats(currentUser.uid, 'client');
        setStats(summaryStats);
      },
      (err) => {
        console.error('❌ Error listening to history:', err);
      }
    );

    return () => unsubscribe();
  }, [currentUser, currentUserRole, ready, navigate, hiddenHistory]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Soft delete (hide from UI but keep in Firestore)
  const handleDelete = async (problemId) => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        hiddenHistory: arrayUnion(problemId),
      });

      setCompletedProblems((prev) => prev.filter((p) => p.id !== problemId));
      setHiddenHistory((prev) => [...prev, problemId]);
    } catch (error) {
      console.error('Error hiding from history:', error);
    }
  };

  const handleExportPDF = () => {
    const content = completedProblems
      .map((p) => `${p.title}\nCompleted: ${p.dateCompleted}\nDomain: ${p.domain}\n\n`)
      .join('\n');

    const blob = new Blob([`Completed Problems History\n\n${content}`], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  if (!ready || !currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-slate-500">Loading history…</p>
        </div>
      </div>
    );
  }

  return (
    <OperationsShell
      user={currentUser}
      roleLabel={getRoleLabel(currentUserRole)}
      onLogout={handleLogout}
      tabs={[{ id: "history", label: "Incident history" }]}
      activeTab="history"
      onTabChange={() => {}}
    >
      <IncidentHistoryView
        tasks={completedProblems}
        stats={stats}
        domains={domains}
        counterpartyLabel="Expert"
        counterpartyField="helperName"
        onDelete={handleDelete}
        onExport={handleExportPDF}
        emptyAction={{ label: "Go to console", onClick: () => navigate('/dashboard-client') }}
      />
    </OperationsShell>
  );
}

export default ClientHistory;
